import React from 'react';
import Select from 'react-select';
import palette from 'src/theme/palette';
import { Button, Checkbox, Stack, Typography, Box } from '@mui/material';

interface SelectComponentProps {
  options: any[];
  isMulti?: boolean;
  placeholder?: string;
  value: any;
  onChange: (value: any) => void;
  label?: string;
  labelStyle?: React.CSSProperties;
  helperText?: string | boolean;
  onClickCheckBox?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  hideSelectedOptions?: boolean;
  closeMenuOnSelect?: boolean;
  formatOptionLabel?: (data: any, formatOptionLabelMeta: any) => React.ReactNode;
  components?: any;
  [key: string]: any;
}

const SelectComponent: React.FC<SelectComponentProps> = ({
  options,
  isMulti = false,
  placeholder = 'Select',
  value,
  onChange,
  label,
  labelStyle,
  helperText,
  disabled = false,
  isDisabled = false,
  isLoading = false,
  formatOptionLabel,
  components,
  ...rest
}) => {
  const isAllSelected = Boolean(value && Array.isArray(value) && value.length === options.length && options.length > 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        {label && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.75rem',
              ...labelStyle,
            }}
          >
            {label}
          </Typography>
        )}
        {isMulti && options.length > 0 && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
              Select All
            </Typography>
            <Checkbox
              size="small"
              checked={isAllSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange(options);
                } else {
                  onChange([]);
                }
              }}
              sx={{ p: 0.25 }}
            />
          </Stack>
        )}
      </Stack>

      <Select
        options={options}
        isMulti={isMulti}
        placeholder={placeholder}
        value={value}
        closeMenuOnSelect={!isMulti}
        onChange={onChange}
        isDisabled={disabled || isDisabled}
        isLoading={isLoading}
        formatOptionLabel={formatOptionLabel}
        components={components}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        {...rest}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isFocused
              ? palette.primary.main
              : helperText
              ? palette.error.main
              : '#E2E8F0',
            minHeight: 40,
            fontSize: '0.875rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : 'none',
            fontWeight: 500,
            backgroundColor: '#FFFFFF',
            borderRadius: 8,
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: palette.primary.main,
            },
            alignItems: 'center',
            cursor: 'pointer',
          }),
          valueContainer: (base) => ({
            ...base,
            flexWrap: 'wrap',
            padding: '2px 8px',
            gap: 4,
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999999,
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999999,
            backgroundColor: '#FFFFFF',
            borderRadius: 10,
            border: '1px solid #E2E8F0',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            marginTop: 4,
          }),
          menuList: (base) => ({
            ...base,
            padding: 4,
            maxHeight: 220,
          }),
          option: (styles, { isFocused, isSelected }) => ({
            ...styles,
            backgroundColor: isSelected
              ? palette.primary.main
              : isFocused
              ? 'rgba(37, 99, 235, 0.08)'
              : 'transparent',
            color: isSelected ? '#FFFFFF' : isFocused ? palette.primary.main : '#1E293B',
            fontSize: '0.8125rem',
            fontWeight: isSelected ? 600 : 500,
            borderRadius: 6,
            padding: '8px 12px',
            cursor: 'pointer',
            margin: '2px 0',
            transition: 'background-color 0.1s ease',
            '&:active': {
              backgroundColor: palette.primary.main,
              color: '#FFFFFF',
            },
          }),
          multiValue: (styles) => ({
            ...styles,
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            borderRadius: 6,
            color: palette.primary.main,
            border: '1px solid rgba(37, 99, 235, 0.25)',
            margin: 2,
          }),
          multiValueLabel: (styles) => ({
            ...styles,
            color: palette.primary.main,
            fontWeight: 600,
            fontSize: '0.75rem',
            padding: '2px 6px',
          }),
          multiValueRemove: (styles) => ({
            ...styles,
            color: palette.primary.main,
            borderRadius: '0 6px 6px 0',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: palette.primary.main,
              color: '#FFFFFF',
            },
          }),
          placeholder: (base) => ({
            ...base,
            color: '#94A3B8',
            fontSize: '0.8125rem',
            margin: 0,
          }),
          input: (base) => ({
            ...base,
            fontSize: '0.8125rem',
            margin: 0,
            padding: 0,
          }),
        }}
      />

      {helperText && typeof helperText === 'string' && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default SelectComponent;
